<script setup lang="ts">
import {ref} from 'vue'
import {useLocalization} from '@hantera/portal-app'

const props = defineProps<{
  secret: string
  onClose?: () => void
}>()

const t = useLocalization('iam.clients')

const secretCopied = ref(false)
const copyFailed = ref(false)
const copyButtonText = ref(t('actions.copySecret'))

async function copySecret() {
  try {
    await navigator.clipboard.writeText(props.secret)
    secretCopied.value = true
    copyFailed.value = false
    copyButtonText.value = '✓ ' + (t('secrets.copied') || 'Copied!')
  } catch (error) {
    console.warn('Clipboard access denied, user must copy manually:', error)
    secretCopied.value = true
    copyFailed.value = true
    copyButtonText.value = 'Please copy manually'
  }
}

function handleClose() {
  if (!secretCopied.value) {
    const confirmed = confirm(t('secrets.closeWithoutCopyWarning'))
    if (!confirmed) return
  }

  props.onClose?.()
}
</script>

<template>
  <div class="secret-modal">
    <div class="box-warning p2">
      {{ t('secrets.modal.warningDetails') }}
    </div>

    <div class="box-content p box-secret">
      <code>{{ secret }}</code>
    </div>

    <div v-if="copyFailed" class="fallback-message">
      <p>Clipboard access restricted. Please select and copy the secret above.</p>
    </div>

    <div class="secret-actions">
      <button @click="copySecret" class="btn-primary btn-large" :disabled="secretCopied && !copyFailed">
        {{ copyButtonText }}
      </button>
    </div>

    <p class="instruction-text">
      {{ t('secrets.modal.instruction') }}
    </p>

    <div class="modal-footer-actions">
      <button 
        @click="handleClose" 
        class="btn-secondary"
        :class="{ 'btn-primary': secretCopied }"
        :disabled="!secretCopied"
      >
        {{ secretCopied ? t('actions.close') : t('secrets.modal.mustCopy') }}
      </button>
    </div>
  </div>
</template>

<style scoped>
.secret-modal {
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.warning-box p {
  margin: 0;
  color: #856404;
  font-size: 0.875rem;
}

.box-secret {
  max-width: 500px;
  margin: auto;
}

.box-secret code {
  font-family: 'Courier New', monospace;
  letter-spacing: 1px;
  word-break: break-all;
}

.fallback-message {
  padding: 0.75rem;
  background: #fff3cd;
  border: 1px solid var(--color-warning);
  border-radius: 4px;
  text-align: center;
}

.fallback-message p {
  margin: 0;
  color: #856404;
  font-size: 0.875rem;
  font-weight: 500;
}

.secret-actions {
  display: flex;
  justify-content: center;
}

.instruction-text {
  text-align: center;
  margin: 0;
  max-width: 500px;
}

.modal-footer-actions {
  display: flex;
  justify-content: center;
  padding-top: 1rem;
}
</style>
